import * as React from "react";
import { useEffect, useRef } from "react";
import { useDiktat } from "./useDiktat";
import { hinweisText } from "./diktatHinweise";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";
import "./diktat.css";

type Props = {
    /** Erkannter Text — der Aufrufer fügt ihn an der gemerkten Stelle ein. */
    onText: (text: string) => void;
    /** Kurz vor der Aufnahme: Cursor/Auswahl merken. */
    onStart?: () => void;
    /** Zusätzliche Klasse für die Platzierung (Toolbar, Rand, Fußzeile). */
    className?: string;
    /** Wohin das Hinweis-Kärtchen aufklappt. */
    hinweisAusrichtung?: "links" | "rechts";
    disabled?: boolean;
};

const Mikro: React.FC = () => (
    <svg width={16} height={16} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7}
        strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="7" y="2.5" width="6" height="10" rx="3" />
        <path d="M4.5 9.5a5.5 5.5 0 0 0 11 0M10 15v2.5M7 17.5h6" />
    </svg>
);

export const zeitText = (s: number): string => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/**
 * Mikrofon-Knopf fürs Diktieren. Erscheint NUR, wenn das Backend einen Dienst
 * eingerichtet hat UND ein Admin Diktieren eingeschaltet hat — sonst rendert
 * er nichts.
 *
 * Klick = Aufnahme starten, nochmal Klick = stoppen. Der Knopf nimmt beim
 * Klicken keinen Fokus (`mousedown` ohne Default), damit Cursor und Auswahl
 * im Textfeld stehen bleiben.
 */
export const DiktatKnopf: React.FC<Props> = ({ onText, onStart, className, hinweisAusrichtung = "links", disabled }) => {
    const d = useDiktat({ onText, onStart });
    const huelleRef = useRef<HTMLSpanElement | null>(null);

    useEffect(() => {
        if (!d.hinweis) return;
        const weg = (e: MouseEvent) => {
            if (huelleRef.current && !huelleRef.current.contains(e.target as Node)) d.hinweisSchliessen();
        };
        const taste = (e: KeyboardEvent) => {
            if (e.key === "Escape") d.hinweisSchliessen();
        };
        // Über alle Dokumente — auch in einem ausgelagerten KMVS-Browserfenster.
        alleDokumente.addEventListener("mousedown", weg as EventListener);
        alleDokumente.addEventListener("keydown", taste as EventListener);
        return () => {
            alleDokumente.removeEventListener("mousedown", weg as EventListener);
            alleDokumente.removeEventListener("keydown", taste as EventListener);
        };
    }, [d.hinweis, d.hinweisSchliessen]);

    if (!d.nutzbar) return null;

    const nimmtAuf = d.phase === "aufnahme";
    const arbeitet = d.phase === "startet" || d.phase === "umwandeln";
    const titel = nimmtAuf
        ? "Aufnahme läuft — klicken zum Beenden"
        : d.phase === "umwandeln"
          ? "Wird in Text umgewandelt …"
          : d.phase === "startet"
            ? "Mikrofon wird geöffnet … (falls der Browser fragt: „Zulassen“)"
            : `Diktieren — klicken, sprechen, nochmal klicken (höchstens ${Math.round(d.maxSekunden / 60)} Min.)`;
    const h = d.hinweis ? hinweisText(d.hinweis) : null;

    return (
        <span ref={huelleRef} className={`dk-huelle${className ? ` ${className}` : ""}`}>
            <button
                type="button"
                className={`dk-knopf${nimmtAuf ? " dk-knopf--aufnahme" : ""}${arbeitet ? " dk-knopf--arbeitet" : ""}`}
                title={titel}
                aria-label={nimmtAuf ? "Diktat beenden" : "Diktieren"}
                aria-pressed={nimmtAuf}
                disabled={disabled || arbeitet}
                onMouseDown={(e) => e.preventDefault()}
                onClick={d.umschalten}
            >
                {arbeitet ? <span className="dk-kreisel" aria-hidden /> : nimmtAuf ? <span className="dk-punkt" aria-hidden /> : <Mikro />}
                {nimmtAuf ? <span className="dk-zeit">{zeitText(d.sekunden)}</span> : null}
                {d.phase === "umwandeln" ? <span className="dk-zeit">Text …</span> : null}
            </button>
            {h ? (
                <div className={`dk-hinweis dk-hinweis--${hinweisAusrichtung}`} role="alertdialog" aria-label={h.titel}>
                    <div className="dk-hinweis-titel">{h.titel}</div>
                    <p className="dk-hinweis-text">{h.text}</p>
                    {h.schritte ? (
                        <ol className="dk-hinweis-schritte">
                            {h.schritte.map((s) => (
                                <li key={s}>{s}</li>
                            ))}
                        </ol>
                    ) : null}
                    <div className="dk-hinweis-fuss">
                        <button type="button" className="dk-hinweis-ok" onMouseDown={(e) => e.preventDefault()} onClick={d.hinweisSchliessen}>
                            Verstanden
                        </button>
                    </div>
                </div>
            ) : null}
        </span>
    );
};
